/**
 * EntitlementPreview
 *
 * Admin smoke-test widget (Appendix E — "Admin UI"): given a sample user's
 * roles and rights, evaluates the manifest's entitlement predicate client-side
 * and shows whether that user would see the app, with a per-clause breakdown.
 *
 * The evaluation mirrors the backend `EntitlementEvaluator` exactly.
 */

import { useMemo, useState } from 'react';
import { IconCheck, IconX, IconUserCheck } from '@tabler/icons-react';
import { TagsInput } from '@mantine/core';
import { Stack, Group, Text, Badge, Paper } from '@/components/ui';
import type { AppEntitlement } from '@actschurch/ccms-sdk';
import { evaluateEntitlement } from './entitlement';

interface EntitlementPreviewProps {
  /** The entitlement predicate to test. */
  entitlement: AppEntitlement;
}

export function EntitlementPreview({ entitlement }: EntitlementPreviewProps) {
  const [sampleRoles, setSampleRoles] = useState<string[]>(['Staff']);
  const [sampleRights, setSampleRights] = useState<string[]>([]);

  const result = useMemo(
    () => evaluateEntitlement(entitlement, sampleRoles, sampleRights),
    [entitlement, sampleRoles, sampleRights]
  );

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Enter a sample user's roles and rights to check whether they would see this app.
      </Text>
      <TagsInput
        label="Sample user roles"
        placeholder="Add a role and press Enter"
        value={sampleRoles}
        onChange={setSampleRoles}
        clearable
      />
      <TagsInput
        label="Sample user rights"
        placeholder="Add a right and press Enter"
        value={sampleRights}
        onChange={setSampleRights}
        clearable
      />
      <Paper
        withBorder
        p="md"
        radius="md"
        style={{
          borderColor: result.entitled
            ? 'var(--mantine-color-green-5)'
            : 'var(--mantine-color-red-5)',
        }}
      >
        <Group gap="xs">
          <IconUserCheck size={18} />
          <Text fw={600}>
            This user would{' '}
            <Text span c={result.entitled ? 'green' : 'red'} fw={700}>
              {result.entitled ? 'see' : 'NOT see'}
            </Text>{' '}
            the app.
          </Text>
        </Group>
        <Stack gap={4} mt="sm">
          {result.reasons.map((r) => (
            <Group key={r.clause} gap="xs" wrap="nowrap">
              {r.satisfied ? (
                <IconCheck size={14} color="var(--mantine-color-green-6)" />
              ) : (
                <IconX size={14} color="var(--mantine-color-red-6)" />
              )}
              <Text size="sm" fw={500}>
                {r.clause}
              </Text>
              {r.required.length > 0 && (
                <Group gap={4}>
                  {r.required.map((v) => (
                    <Badge key={v} size="xs" variant="light" color={r.satisfied ? 'green' : 'red'}>
                      {v}
                    </Badge>
                  ))}
                </Group>
              )}
            </Group>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
}

export default EntitlementPreview;
